<template>
  <form class="supplier-form" @submit.prevent="onSubmit">
    <UiCard>
      <div class="supplier-form__header">
        <h3>{{ isEditing ? 'Редактирование поставщика' : 'Создание поставщика' }}</h3>
      </div>

      <div class="supplier-form__section">
        <h4>Основная информация</h4>
        <div class="supplier-form__grid">
          <div class="supplier-form__span-8">
            <label class="supplier-form__label">Полное наименование организации</label>
            <UiInput v-model="form.businessDetails.legalName" />
            <span v-if="errors.legalName" class="supplier-form__error">
              {{ errors.legalName }}
            </span>
          </div>
          <div>
            <label class="supplier-form__label">ИНН</label>
            <UiInput
              v-model="form.businessDetails.inn"
              input-filter="digits"
              maxlength="12"
              @blur="validateInn"
            />
            <span v-if="errors.inn" class="supplier-form__error">{{ errors.inn }}</span>
          </div>
          <div>
            <label class="supplier-form__label">КПП</label>
            <UiInput v-model="form.businessDetails.kpp" input-filter="digits" maxlength="9" />
            <span v-if="errors.kpp" class="supplier-form__error">{{ errors.kpp }}</span>
          </div>
          <div>
            <label class="supplier-form__label">ОГРН</label>
            <UiInput v-model="form.businessDetails.ogrn" input-filter="digits" maxlength="15" />
            <span v-if="errors.ogrn" class="supplier-form__error">{{ errors.ogrn }}</span>
          </div>
          <div class="supplier-form__span-12">
            <label class="supplier-form__label">Юридический адрес</label>
            <UiInput v-model="form.businessDetails.legalAddress" />
          </div>
        </div>
      </div>

      <div class="supplier-form__divider"></div>

      <div class="supplier-form__section">
        <h4>Контактная информация</h4>
        <div class="supplier-form__grid">
          <div>
            <label class="supplier-form__label">Email</label>
            <UiInput v-model="form.contactInfo.email" />
            <span v-if="errors.email" class="supplier-form__error">{{ errors.email }}</span>
          </div>
          <div>
            <label class="supplier-form__label">Телефон</label>
            <UiInput v-model="form.contactInfo.phone" input-filter="phone" placeholder="+7 999 999-99-99" />
            <span v-if="errors.phone" class="supplier-form__error">{{ errors.phone }}</span>
          </div>
          <div>
            <label class="supplier-form__label">Контактное лицо</label>
            <UiInput v-model="form.contactInfo.contactPerson" />
          </div>
          <div>
            <label class="supplier-form__label">Должность контактного лица</label>
            <UiInput v-model="form.contactInfo.contactPosition" />
          </div>
          <div>
            <label class="supplier-form__label">Сайт</label>
            <UiInput v-model="form.contactInfo.website" placeholder="https://example.com" />
            <span v-if="errors.website" class="supplier-form__error">{{ errors.website }}</span>
          </div>
          <div>
            <label class="supplier-form__label">Фактический адрес</label>
            <UiInput v-model="form.contactInfo.address" />
          </div>
        </div>
      </div>

      <div class="supplier-form__divider"></div>

      <div class="supplier-form__section">
        <h4>Дополнительная информация</h4>
        <label class="supplier-form__label">Описание деятельности</label>
        <textarea v-model="form.description" class="supplier-form__textarea"></textarea>
        <span v-if="errors.description" class="supplier-form__error">{{ errors.description }}</span>

        <div v-if="isEditing" class="supplier-form__commission">
          <label class="supplier-form__label">Комиссия (%)</label>
          <UiInput v-model="form.commissionRate" input-filter="decimal" />
          <span v-if="errors.commission" class="supplier-form__error">{{ errors.commission }}</span>
        </div>
      </div>

      <div class="supplier-form__actions">
        <UiButton
          type="button"
          variant="secondary"
          :disabled="Boolean(loading)"
          @click="$emit('cancel')"
        >
          Отмена
        </UiButton>
        <UiButton type="submit" variant="secondary" :disabled="Boolean(loading) || !isFormValid">
          {{ isEditing ? 'Сохранить' : 'Создать' }}
        </UiButton>
      </div>
    </UiCard>
  </form>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch, onMounted } from 'vue'
import type { Supplier, CreateSupplierData, UpdateSupplierData } from '@/types/supplier'
import UiButton from '@/shared/ui/UiButton.vue'
import UiCard from '@/shared/ui/UiCard.vue'
import UiInput from '@/shared/ui/UiInput.vue'

interface Props {
  supplier?: Supplier
  loading?: boolean
}

interface Emits {
  (e: 'submit', data: CreateSupplierData | UpdateSupplierData): void
  (e: 'cancel'): void
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const emit = defineEmits<Emits>()

const isFormValid = ref(false)
const isEditing = computed(() => !!props.supplier)

const form = reactive({
  businessDetails: {
    legalName: '',
    inn: '',
    kpp: '',
    ogrn: '',
    legalAddress: '',
  },
  contactInfo: {
    email: '',
    phone: '',
    contactPerson: '',
    contactPosition: '',
    website: '',
    address: '',
  },
  description: '',
  commissionRate: 0,
})

const errors = reactive({
  legalName: '',
  inn: '',
  kpp: '',
  ogrn: '',
  email: '',
  phone: '',
  website: '',
  description: '',
  commission: '',
})

const setError = (field: keyof typeof errors, message: string) => {
  errors[field] = message
}

const clearErrors = () => {
  Object.keys(errors).forEach((key) => {
    errors[key as keyof typeof errors] = ''
  })
}

const validateForm = () => {
  clearErrors()

  let valid = true

  if (!form.businessDetails.legalName) {
    setError('legalName', 'Поле обязательно для заполнения')
    valid = false
  }

  const innDigits = form.businessDetails.inn.replace(/\D/g, '')
  if (!innDigits) {
    setError('inn', 'ИНН обязателен')
    valid = false
  } else if (innDigits.length !== 10 && innDigits.length !== 12) {
    setError('inn', 'ИНН должен содержать 10 или 12 цифр')
    valid = false
  }

  if (form.businessDetails.kpp) {
    const kppDigits = form.businessDetails.kpp.replace(/\D/g, '')
    if (kppDigits.length !== 9) {
      setError('kpp', 'КПП должен содержать 9 цифр')
      valid = false
    }
  }

  if (form.businessDetails.ogrn) {
    const ogrnDigits = form.businessDetails.ogrn.replace(/\D/g, '')
    if (ogrnDigits.length !== 13 && ogrnDigits.length !== 15) {
      setError('ogrn', 'ОГРН должен содержать 13 или 15 цифр')
      valid = false
    }
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!form.contactInfo.email) {
    setError('email', 'Поле обязательно для заполнения')
    valid = false
  } else if (!emailPattern.test(form.contactInfo.email)) {
    setError('email', 'Некорректный email')
    valid = false
  }

  if (form.contactInfo.phone) {
    const phoneDigits = form.contactInfo.phone.replace(/\D/g, '')
    if (phoneDigits.length < 10) {
      setError('phone', 'Некорректный телефон')
      valid = false
    }
  }

  if (form.contactInfo.website) {
    try {
      new URL(form.contactInfo.website)
    } catch {
      setError('website', 'Некорректный URL')
      valid = false
    }
  }

  if (form.description && form.description.length > 1000) {
    setError('description', 'Максимум 1000 символов')
    valid = false
  }

  if (isEditing.value) {
    if (form.commissionRate < 0 || form.commissionRate > 100) {
      setError('commission', 'Комиссия должна быть от 0 до 100%')
      valid = false
    }
  }

  isFormValid.value = valid
  return valid
}

const initializeForm = () => {
  if (props.supplier) {
    Object.assign(form.businessDetails, props.supplier.businessDetails)
    Object.assign(form.contactInfo, props.supplier.contactInfo)
    form.description = props.supplier.description || ''
    form.commissionRate = props.supplier.commissionRate || 0
  }
  validateForm()
}

const validateInn = () => {
  validateForm()
}

const formatPhone = (value: string) => {
  if (!value) return ''
  const digits = value.replace(/\D/g, '')
  if (digits.startsWith('8')) {
    return '+7' + digits.substring(1)
  }
  if (digits.startsWith('7')) {
    return '+' + digits
  }
  return value
}

const onSubmit = () => {
  if (!validateForm()) return

  if (form.contactInfo.phone) {
    form.contactInfo.phone = formatPhone(form.contactInfo.phone)
  }

  const cleanForm: CreateSupplierData | UpdateSupplierData = {
    businessDetails: {
      legalName: form.businessDetails.legalName,
      inn: form.businessDetails.inn,
      ...(form.businessDetails.kpp ? { kpp: form.businessDetails.kpp } : {}),
      ...(form.businessDetails.ogrn ? { ogrn: form.businessDetails.ogrn } : {}),
      ...(form.businessDetails.legalAddress
        ? { legalAddress: form.businessDetails.legalAddress }
        : {}),
    },
    contactInfo: {
      email: form.contactInfo.email,
      ...(form.contactInfo.phone ? { phone: form.contactInfo.phone } : {}),
      ...(form.contactInfo.contactPerson ? { contactPerson: form.contactInfo.contactPerson } : {}),
      ...(form.contactInfo.contactPosition
        ? { contactPosition: form.contactInfo.contactPosition }
        : {}),
      ...(form.contactInfo.website ? { website: form.contactInfo.website } : {}),
      ...(form.contactInfo.address ? { address: form.contactInfo.address } : {}),
    },
    ...(form.description ? { description: form.description } : {}),
    ...(isEditing.value && form.commissionRate > 0 ? { commissionRate: form.commissionRate } : {}),
  }

  emit('submit', cleanForm)
}

onMounted(() => {
  initializeForm()
})

watch(
  () => props.supplier,
  () => {
    initializeForm()
  },
  { deep: true },
)
</script>

<style scoped>
.supplier-form {
  max-width: 900px;
  margin: 0 auto;
}

.supplier-form__header {
  margin-bottom: 12px;
}

.supplier-form__section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.supplier-form__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.supplier-form__span-8 {
  grid-column: span 2;
}

.supplier-form__span-12 {
  grid-column: 1 / -1;
}

.supplier-form__label {
  display: block;
  font-size: 12px;
  color: #586578;
  margin-bottom: 6px;
}

.supplier-form__divider {
  height: 1px;
  background: #e2e8f0;
  margin: 16px 0;
}

.supplier-form__textarea {
  width: 100%;
  min-height: 120px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 10px;
  font: inherit;
  resize: vertical;
}

.supplier-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}

.supplier-form__error {
  color: #ef4444;
  font-size: 12px;
}

.supplier-form__commission {
  margin-top: 12px;
}

@media (max-width: 600px) {
  .supplier-form__span-8 {
    grid-column: 1 / -1;
  }
}
</style>
